import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import Button from '../components/customButtons/Button';

const ResendVerification = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [email, setEmail] = useState(location.state?.email || '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [resendCount, setResendCount] = useState(0);
  const [canResend, setCanResend] = useState(true);

  // Use the same API URL as your AuthContext
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  // Reset resend limit after 1 minute
  useEffect(() => {
    if (resendCount >= 3) {
      setCanResend(false);
      const timer = setTimeout(() => {
        setResendCount(0);
        setCanResend(true);
      }, 60000); // 1 minute cooldown

      return () => clearTimeout(timer);
    }
  }, [resendCount]);

  const handleResend = async (e) => {
    e.preventDefault();
    
    if (!email) {
      setMessage({ type: 'error', text: 'Email address is required' });
      return;
    }

    if (!canResend) {
      setMessage({ 
        type: 'error', 
        text: 'Please wait 1 minute before requesting another verification email.' 
      });
      return;
    }

    setIsSubmitting(true);
    setMessage({ type: '', text: '' });

    try {
      console.log('🔄 Sending resend verification request to:', `${API_URL}/api/auth/resend-verification`);
      
      const response = await fetch(`${API_URL}/api/auth/resend-verification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      console.log('📨 Response status:', response.status);

      // Check if response is OK before parsing JSON
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Server response error:', errorText);
        throw new Error(`Server error: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      console.log('✅ Resend verification response:', data);

      if (data.success) {
        setResendCount(prev => prev + 1);
        setMessage({ 
          type: 'success', 
          text: data.message || 'Verification email sent successfully! Please check your inbox.' 
        });
      } else {
        setMessage({ 
          type: 'error', 
          text: data.error || 'Failed to resend verification email. Please try again.' 
        });
      }
    } catch (error) {
      console.error('❌ Resend verification error:', error);
      
      let errorMessage = 'Network error. Please check your connection and try again.';
      
      if (error.message.includes('Server error')) {
        errorMessage = 'Server error. Please try again later.';
      } else if (error.message.includes('Unexpected token')) {
        errorMessage = 'Cannot connect to server. Please make sure the backend is running.';
      }
      
      setMessage({ 
        type: 'error', 
        text: errorMessage 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-8 font-inter">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        
        {/* Card Container */}
        <div className="bg-white py-8 px-6 shadow-sm rounded-lg sm:px-10 border border-gray-100">
          
          {/* Header */}
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-teal-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-teal-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
              </svg>
            </div>
            
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Resend Verification Email
            </h2>
            <p className="text-gray-600 text-sm">
              Enter your email address to receive a new verification link.
            </p>
          </div>

          {/* Message Display */}
          {message.text && (
            <div className={`mb-4 p-3 rounded-md text-sm ${
              message.type === 'success' 
                ? 'bg-green-50 text-green-700 border border-green-200' 
                : 'bg-red-50 text-red-600 border border-red-200'
            }`}>
              {message.text}
            </div>
          )}

          {/* Resend Limit Warning */}
          {resendCount >= 2 && (
            <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
              <p className="text-yellow-700 text-sm">
                <strong>Notice:</strong> You've requested {resendCount} verification emails. 
                {resendCount >= 3 && ' Please wait 1 minute before trying again.'}
              </p>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleResend} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email Address
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="block w-full border border-gray-300 rounded-md shadow-sm py-2.5 px-3 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm"
                required
                disabled={isSubmitting}
              />
            </div>

            <Button
              type="submit"
              disabled={isSubmitting || !canResend}
              className="w-full py-2.5"
              variant="primary"
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Sending...
                </span>
              ) : (
                'Send Verification Email'
              )}
            </Button>
          </form>

          {/* Helpful Tips */}
          <div className="mt-6 p-4 bg-gray-50 rounded-md">
            <h3 className="font-medium text-gray-900 text-sm mb-2">Tips:</h3>
            <ul className="text-gray-600 text-sm space-y-1">
              <li>• Check your spam or junk folder</li>
              <li>• Make sure you entered the correct email address</li>
              <li>• Wait a few minutes for the email to arrive</li>
              <li>• Contact support if you continue having issues</li>
            </ul>
          </div>

          {/* Navigation Links */}
          <div className="mt-6 text-center space-y-3">

            <div className="pt-4 border-t border-gray-200">
              <p className="text-xs text-gray-500">
                Need help? Contact us at <span className="text-teal-600">support@cropser.com</span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResendVerification;